import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ApiUrls from '@api/apiUrls';
import { apiImageUserService } from '@api/services/imageUserService.apiService';
import type { INotification } from '@api/services/notificationsUser.apiService.interfaces';
import { Button } from '@components-library/Button';
import { PresentIcon } from '@components-library/Icons/iconsComponents';
import { DocumentView } from '@components-shared/DocumentsViewer';
import { Modal } from '@components-shared/modal';
import { keyMap } from '@locales/lokalise/keyMap';
import { registerExceptionInTrackingSystem } from '@utils/errors';
import cn from 'classnames';
import { observer } from 'mobx-react';
import { BonusPopupTimer } from './BonusPopupTimer';
import styles from './bonusPopup.module.scss';

interface IBonusPopupProps {
  onAccept: () => void;
  onReject: () => void;
  selectedNotification?: INotification | null;
}

export const BonusPopup = observer(({ onAccept, onReject, selectedNotification }: IBonusPopupProps) => {
  const { i18n } = useTranslation();
  const [showImageFallback, setShowImageFallback] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const imageSrc = apiImageUserService.getMembersAreaImageSrc({
    imageUniqueId: selectedNotification?.imageUniqueId ?? '',
  });

  const handleSetImageLoaded = (options?: { error?: boolean }) => {
    setIsImageLoaded(true);
    if (options?.error) {
      setShowImageFallback(true);

      registerExceptionInTrackingSystem(`Failed to load the image. Switch to show fallback. src: ${imageSrc}`);
    }
  };

  /**
   * @TODO: temp implementation. The whole solution is part of other tasks.
   * It is supposed to have multiple documents within popup
   */
  const termsAndConditionsDocument = selectedNotification?.documents.find(
    (document) => document.type === 'BirthdayBonusTermsAndConditions',
  );

  return !selectedNotification ? null : (
    <Modal classNameContainer={cn({ [styles.imageIsNotReady]: !isImageLoaded })} closeHandler={onReject}>
      <>
        <div className={styles.bonusPopupContainer}>
          <div className={styles.birthdayIconContainer}>
            {showImageFallback ? (
              <PresentIcon className={styles.birthdayIcon} />
            ) : (
              <img
                className={styles.birthdayImage}
                src={imageSrc}
                onError={() => handleSetImageLoaded({ error: true })}
                onLoad={() => handleSetImageLoaded()}
              />
            )}
          </div>

          <div className={styles.message}>
            <BonusPopupTimer className={styles.timerWithinPopup} expirationTime={selectedNotification.expirationDate} />
            <h2 className={styles.title}>{selectedNotification.subject}</h2>
            <div dangerouslySetInnerHTML={{ __html: selectedNotification.body }} />
            {termsAndConditionsDocument && (
              <div className={styles.documentsContainer}>
                <DocumentView
                  src={`${ApiUrls.legalFileItemUser.download}?id=${termsAndConditionsDocument.legalFileId}`}
                  fileType={termsAndConditionsDocument.fileType}
                  withoutDocumentPreview={true}
                  useCustomFullSize={true}
                  buttonCustomTitle={termsAndConditionsDocument.description}
                  className={styles.documentLink}
                />
              </div>
            )}
          </div>
          <div className={styles.buttonWrapper}>
            <Button onClick={onReject} className={cn('btn light--button active', styles.closeButton)}>
              {i18n.t(keyMap.commonClose)}
            </Button>
            <Button onClick={onAccept} className="form__button">
              {i18n.t(keyMap.bonusesConfirmBonus)}
            </Button>
          </div>
        </div>
      </>
    </Modal>
  );
});
